import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { useBackofficePreference } from '@/preferences';
import type { BackofficePageMode } from './BackofficePageModeActionMenu';

export interface BuiltInDetailTab<TTabId extends string> {
  id: TTabId;
  label: string;
}

export interface CustomDetailTab {
  id: string;
  label: string;
}

interface DetailTabsPreference {
  [key: string]: unknown;
  hiddenBuiltInTabIds?: string[] | undefined;
  customTabs?: CustomDetailTab[] | undefined;
}

const DEFAULT_DETAIL_TABS_PREFERENCE: DetailTabsPreference = {};

export interface UseCustomizableDetailTabsOptions<TTabId extends string> {
  preferenceScope: string;
  builtInTabs: BuiltInDetailTab<TTabId>[];
  defaultTabId: TTabId;
  defaultHrefTabId?: TTabId | undefined;
  clearSearchParamsByTabId?: Partial<Record<TTabId, string[]>> | undefined;
  basePath: string;
  pageMode: BackofficePageMode;
}

function createCustomTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `custom-${crypto.randomUUID()}`;
  }
  return `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useCustomizableDetailTabs<TTabId extends string>({
  preferenceScope,
  builtInTabs,
  defaultTabId,
  defaultHrefTabId = defaultTabId,
  clearSearchParamsByTabId,
  basePath,
  pageMode,
}: UseCustomizableDetailTabsOptions<TTabId>) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preference = useBackofficePreference<DetailTabsPreference>(preferenceScope, {
    defaultValue: DEFAULT_DETAIL_TABS_PREFERENCE,
    debounceMs: 300,
  });
  const hiddenBuiltInTabIds = new Set(preference.value.hiddenBuiltInTabIds ?? []);
  const visibleBuiltInTabs = builtInTabs.filter((tab) => !hiddenBuiltInTabIds.has(tab.id));
  const customTabs = preference.value.customTabs ?? [];
  const visibleTabs = [...visibleBuiltInTabs, ...customTabs];
  const requestedTabId = searchParams.get('tab') ?? defaultTabId;
  const activeTabId = visibleTabs.some((tab) => tab.id === requestedTabId)
    ? requestedTabId
    : (visibleTabs[0]?.id ?? defaultTabId);
  const activeCustomTab = customTabs.find((tab) => tab.id === activeTabId);
  const activeBuiltInTabId = builtInTabs.some((tab) => tab.id === activeTabId)
    ? (activeTabId as TTabId)
    : null;
  const hiddenBuiltInTabs = builtInTabs.filter((tab) => hiddenBuiltInTabIds.has(tab.id));

  function getTabHref(tabId: string, updates: Record<string, string | null> = {}) {
    const next = new URLSearchParams(searchParams);
    if (tabId === defaultHrefTabId) {
      next.delete('tab');
    } else {
      next.set('tab', tabId);
    }
    for (const param of clearSearchParamsByTabId?.[tabId as TTabId] ?? []) {
      next.delete(param);
    }
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    const query = next.toString();
    return query ? `${basePath}?${query}` : basePath;
  }

  function hideBuiltInTab(tabId: TTabId) {
    if (visibleTabs.length <= 1) {
      return;
    }
    if (activeTabId === tabId) {
      const nextTab = visibleTabs.find((tab) => tab.id !== tabId);
      if (nextTab) {
        navigate(getTabHref(nextTab.id));
      }
    }
    preference.setValue((current) => ({
      ...current,
      hiddenBuiltInTabIds: Array.from(new Set([...(current.hiddenBuiltInTabIds ?? []), tabId])),
    }));
  }

  function restoreBuiltInTab(tabId: TTabId) {
    preference.setValue((current) => ({
      ...current,
      hiddenBuiltInTabIds: (current.hiddenBuiltInTabIds ?? []).filter((id) => id !== tabId),
    }));
  }

  function addCustomTab(label: string) {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      return;
    }
    const tabId = createCustomTabId();
    preference.setValue((current) => ({
      ...current,
      customTabs: [...(current.customTabs ?? []), { id: tabId, label: trimmedLabel }],
    }));
    navigate(getTabHref(tabId));
  }

  function removeCustomTab(tabId: string) {
    if (visibleTabs.length <= 1) {
      return;
    }
    if (activeTabId === tabId) {
      const nextTab = visibleTabs.find((tab) => tab.id !== tabId);
      if (nextTab) {
        navigate(getTabHref(nextTab.id));
      }
    }
    preference.setValue((current) => ({
      ...current,
      customTabs: (current.customTabs ?? []).filter((tab) => tab.id !== tabId),
    }));
  }

  function renameCustomTab(tabId: string, label: string) {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      return;
    }
    preference.setValue((current) => ({
      ...current,
      customTabs: (current.customTabs ?? []).map((tab) =>
        tab.id === tabId ? { ...tab, label: trimmedLabel } : tab
      ),
    }));
  }

  return useMemo(
    () => ({
      activeBuiltInTabId,
      activeCustomTab,
      activeTabId,
      addCustomTab,
      customTabs,
      getTabHref,
      hiddenBuiltInTabs,
      hideBuiltInTab,
      loaded: preference.loaded,
      pageMode,
      removeCustomTab,
      renameCustomTab,
      restoreBuiltInTab,
      visibleTabs,
    }),
    [
      activeBuiltInTabId,
      activeCustomTab,
      activeTabId,
      customTabs,
      hiddenBuiltInTabs,
      pageMode,
      preference.loaded,
      visibleTabs,
    ]
  );
}

export interface CustomizableDetailTabsProps<TTabId extends string> {
  tabs: ReturnType<typeof useCustomizableDetailTabs<TTabId>>;
}

export function CustomizableDetailTabs<TTabId extends string>({
  tabs,
}: CustomizableDetailTabsProps<TTabId>) {
  const [catalogOpen, setCatalogOpen] = useState(false);

  function getTabClassName(tabId: string) {
    return [
      'inline-flex items-center gap-1 px-1 pb-3 text-body font-semibold',
      tabs.activeTabId === tabId
        ? 'border-b-2 border-neutral-900 text-neutral-900'
        : 'border-b-2 border-transparent text-neutral-500 hover:text-backoffice-600',
    ].join(' ');
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-neutral-200">
        {tabs.visibleTabs.map((tab) => {
          const isCustomTab = tab.id.startsWith('custom-');
          return (
            <span key={tab.id} className="inline-flex items-center gap-1">
              <Link
                to={tabs.getTabHref(tab.id)}
                className={getTabClassName(tab.id)}
                aria-current={tabs.activeTabId === tab.id ? 'page' : undefined}
              >
                {tab.label}
              </Link>
              {tabs.pageMode === 'edit' && tabs.loaded ? (
                <button
                  type="button"
                  className="focus-ring mb-3 inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 hover:bg-red-50 hover:text-red-700"
                  aria-label={`${isCustomTab ? 'Remove' : 'Hide'} ${tab.label} tab`}
                  onClick={() => {
                    if (isCustomTab) {
                      tabs.removeCustomTab(tab.id);
                    } else {
                      tabs.hideBuiltInTab(tab.id as TTabId);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              ) : null}
            </span>
          );
        })}
        {tabs.pageMode === 'edit' && tabs.loaded ? (
          <button
            type="button"
            className="focus-ring mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-dashed border-neutral-300 text-neutral-500 hover:bg-backoffice-50 hover:text-backoffice-700"
            aria-label="Add tab"
            title="Add tab"
            onClick={() => setCatalogOpen(true)}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {catalogOpen ? <TabCatalogModal tabs={tabs} onClose={() => setCatalogOpen(false)} /> : null}
    </>
  );
}

interface TabCatalogModalProps<TTabId extends string> {
  tabs: ReturnType<typeof useCustomizableDetailTabs<TTabId>>;
  onClose: () => void;
}

function TabCatalogModal<TTabId extends string>({ tabs, onClose }: TabCatalogModalProps<TTabId>) {
  const [newTabLabel, setNewTabLabel] = useState('');
  const [editingLabels, setEditingLabels] = useState<Record<string, string>>({});

  return (
    <div
      className="tile-settings-modal fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-neutral-950/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="detail-tab-catalog-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 id="detail-tab-catalog-title" className="font-inter-tight text-xl font-semibold">
            Tabs
          </h2>
          <button
            type="button"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-backoffice-50"
            aria-label="Close tab manager"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <div>
            <h3 className="text-body font-semibold text-neutral-900">Add custom tab</h3>
            <div className="mt-2 flex gap-2">
              <input
                className="focus-ring min-w-0 flex-1 rounded-md border border-neutral-200 px-3 py-2 text-body"
                value={newTabLabel}
                aria-label="Custom tab name"
                onChange={(event) => setNewTabLabel(event.target.value)}
              />
              <button
                type="button"
                className="focus-ring inline-flex h-10 items-center rounded-md border border-backoffice-500 bg-backoffice-500 px-3 text-small font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!newTabLabel.trim()}
                onClick={() => {
                  tabs.addCustomTab(newTabLabel);
                  setNewTabLabel('');
                  onClose();
                }}
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-body font-semibold text-neutral-900">Restore built-in tabs</h3>
            <div className="mt-2 space-y-2">
              {tabs.hiddenBuiltInTabs.length === 0 ? (
                <p className="rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-body text-neutral-500">
                  All built-in tabs are visible.
                </p>
              ) : (
                tabs.hiddenBuiltInTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className="focus-ring flex w-full items-center justify-between rounded-md border border-neutral-200 px-4 py-3 text-left text-body font-semibold text-neutral-900 hover:bg-backoffice-50"
                    onClick={() => tabs.restoreBuiltInTab(tab.id)}
                  >
                    {tab.label}
                    <Plus className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                  </button>
                ))
              )}
            </div>
          </div>

          {tabs.customTabs.length ? (
            <div>
              <h3 className="text-body font-semibold text-neutral-900">Custom tabs</h3>
              <div className="mt-2 space-y-2">
                {tabs.customTabs.map((tab) => (
                  <div key={tab.id} className="flex gap-2 rounded-md border border-neutral-200 p-2">
                    <input
                      className="focus-ring min-w-0 flex-1 rounded-md border border-neutral-200 px-3 py-2 text-body"
                      value={editingLabels[tab.id] ?? tab.label}
                      aria-label={`Rename ${tab.label} tab`}
                      onChange={(event) =>
                        setEditingLabels((current) => ({
                          ...current,
                          [tab.id]: event.target.value,
                        }))
                      }
                      onBlur={(event) => tabs.renameCustomTab(tab.id, event.target.value)}
                    />
                    <button
                      type="button"
                      className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-md text-neutral-500 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Remove ${tab.label} tab`}
                      onClick={() => tabs.removeCustomTab(tab.id)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
